import {diApiNa} from '@di-shared/api/di';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import {formatNumberWithCommas} from '@di-shared/utils';
import {isSameYearAndMonth, monthOfInterval, parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

const GRAPH_LENGTH = 3;

// 숫자를 포맷팅하여 999999를 초과하면 "+999999"로 표시
// 0이 들어오면 -로 표시
export function formatNumberWithLimitAndCommas(value: number | string): string {
  const MAX_DISPLAY_VALUE = 999999;

  if (typeof value === 'string') {
    return value;
  }
  if (value === 0) {
    return '-';
  }
  if (value > MAX_DISPLAY_VALUE) {
    return `+${formatNumberWithCommas(MAX_DISPLAY_VALUE)}`;
  }
  return formatNumberWithCommas(value);
}

export default function useDrivingInfoNa({vin, selectedDate = new Date()}: {vin: string; selectedDate?: Date}) {
  const {data: drivingData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.drivingHistory.retrieveDrivingInfo), vin],
    queryFn: () => diApiNa.drivingHistory.retrieveDrivingInfo({vin}),
    select: ({data}) => {
      // 빈 배열이면 당월 데이터만 채워서 반환
      if (!data.drivingDistances || data.drivingDistances.length === 0) {
        return {
          ...data,
          drivingDistances: [
            {
              date: getYYYYMMFormat(new Date()),
              totalDistance: 0,
            },
          ],
        };
      }

      const earliestDate = parseDateWithFormat(data.drivingDistances[0].date);
      if (!earliestDate) {
        throw new ExpectedError('earliestDate parse error');
      }

      // AS-IS 주행기록 월별 데이터가 내려오면서, 주행 없는 경우는 BE에서 빈값을 배열에 채워서 내려줌
      // TO-BE BE 원천이 변경되면서 스펙이 변경 됨, FE에서 빈값을 배열에 직접 채우는 것으로 수정
      const filledDrivingDistances = monthOfInterval(earliestDate, new Date()).map((month) => {
        const existingData = data.drivingDistances.find((item) => item.date === getYYYYMMFormat(month));
        if (existingData) {
          return existingData;
        } else {
          return {
            date: getYYYYMMFormat(month),
            totalDistance: 0,
          };
        }
      });

      const maxDrivingDistance = filledDrivingDistances.slice(-GRAPH_LENGTH);

      return {
        ...data,
        drivingDistances: maxDrivingDistance,
      };
    },
  });

  const verifiedDrivingDistances = drivingData.drivingDistances.map((driving) => {
    const verifiedDate = parseDateWithFormat(driving.date);
    if (!verifiedDate) {
      throw new ExpectedError('drivingDistances parse error');
    }
    return {
      ...driving,
      verifiedDate,
    };
  });

  const prevDrivingDistance = verifiedDrivingDistances.find((driving) =>
    isSameYearAndMonth(driving.verifiedDate, subDate(selectedDate, {months: 1}))
  );
  const currentDrivingDistance = verifiedDrivingDistances.find((driving) => isSameYearAndMonth(driving.verifiedDate, selectedDate));
  if (!currentDrivingDistance) {
    throw new ExpectedError('currentDrivingDistance error');
  }

  const isCurrentDrivingDistanceNone = currentDrivingDistance.totalDistance === 0;

  return {
    drivingData,
    verifiedDrivingDistances,
    prevDrivingDistance,
    currentDrivingDistance,
    isCurrentDrivingDistanceNone,
  };
}
